import { c as _c } from "react/compiler-runtime";
import { Dropdown, IconButton, openDialog, useTranslationContext } from 'platform/components';
import { suffixTestId, TestIdProps } from 'shared/web';
import { ActivePreset, Preset } from '../../../../types/Api';
import { isPresetCustom } from '../../../../utils/isPresetCustom';
import { EditPresetDialog } from '../../../PresetPanel/components/EditPresetDialog/EditPresetDialog';
import { PresetButtonActions } from '../../../PresetPanel/components/PresetButtonActions/PresetButtonActions';
export type MobilePresetsDialogProps = TestIdProps & {
  presets: Preset[];
  activePreset: ActivePreset;
  isPresetChanged: boolean;
  onActivate: (preset: Preset) => Promise<void>;
  onDeactivate: () => Promise<void>;
  onCreate: (name: string, desc: string) => Promise<void>;
  onUpdate: (presetId: string) => void;
  onDelete: (presetId: string) => void;
  onDuplicate: (presetId: string) => void;
  onRename: (presetId: string, title: string, description: string) => Promise<void>;
  onResetOverrides: () => void;
  onUpdatePosition: (presetId: string, from: number, to: number) => void;
};

/**
 * Preset panel content rendered inside the mobile sidebar.
 *
 * Shows:
 * - An "Add preset" row at the top.
 * - A scrollable list of all presets. The active preset is highlighted in blue.
 *   Tapping a row activates / deactivates it.
 * - Per-preset `Dropdown` with `PresetButtonActions` (update, reset, edit, duplicate,
 *   reorder, delete) — same actions as the desktop pill overflow menu.
 */
export function MobilePresetsDialog(props) {
  const $ = _c(28);
  const t = useTranslationContext();
  const activePresetId = isPresetCustom(props.activePreset) ? props.activePreset.id : undefined;
  let t0;
  if ($[0] !== props || $[1] !== t) {
    t0 = () => openDialog(<EditPresetDialog onSubmit={props.onCreate} data-testid={suffixTestId("createPreset", props)} />, {
      id: "createPreset",
      title: t("general.presets.createPreset"),
      "data-testid": suffixTestId("createPreset", props),
      size: "small",
      scrollBehavior: "outside"
    });
    $[0] = props;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== props) {
    t1 = suffixTestId("addPresetButton", props);
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = <IconButton icon="content/add" size="small" onClick={t0} data-testid={t1} />;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t) {
    t3 = t("general.presets.createPreset");
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <span className="text-sm text-gray-500">{t3}</span>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 flex-shrink-0">{t2}{t4}</div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== activePresetId || $[16] !== props || $[17] !== t) {
    t6 = props.presets.map((preset, index) => {
      const isActive = preset.id === activePresetId;
      return <div key={preset.id} className={["flex items-center gap-2 px-2 py-2 rounded transition-colors", isActive ? "bg-blue-50" : "hover:bg-gray-50"].join(" ")}><button type="button" className="flex-1 text-left text-sm truncate" onClick={() => {
          if (isActive) {
            props.onDeactivate();
          } else {
            props.onActivate(preset);
          }
        }}><span className={isActive ? "text-blue-700 font-medium" : "text-gray-700"}>{preset.title}</span></button><Dropdown isLazy={true} strategy="fixed" dropdownControl={<IconButton icon="navigation/more_vert" size="small" data-testid={suffixTestId(`preset-${preset.title}-actions`, props)} />} data-testid={suffixTestId(`preset-${preset.title}-dropdown`, props)}><PresetButtonActions preset={preset} index={index} presetChanged={props.isPresetChanged} activePresetId={activePresetId} updatePreset={props.onUpdate} deletePreset={props.onDelete} duplicatePreset={props.onDuplicate} resetOverrides={props.onResetOverrides} updatePresetPosition={props.onUpdatePosition} openEditModal={() => openDialog(<EditPresetDialog presetToEdit={preset} onSubmit={(title, description) => props.onRename(preset.id, title, description)} data-testid={suffixTestId("editPreset", props)} />, {
            id: "editPreset",
            title: t("general.presets.editPreset", {
              presetName: preset.title
            }),
            "data-testid": suffixTestId("editPreset", props),
            size: "small",
            scrollBehavior: "outside"
          })} presetsLength={props.presets.length} data-testid={suffixTestId(`preset-${preset.title}`, props)} /></Dropdown></div>;
    });
    $[15] = activePresetId;
    $[16] = props;
    $[17] = t;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== props.presets.length || $[20] !== t) {
    t7 = props.presets.length === 0 && <div className="text-sm text-gray-400 text-center py-6">{t("page.datagrid.labels.noPresets")}</div>;
    $[19] = props.presets.length;
    $[20] = t;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t6 || $[23] !== t7) {
    t8 = <div className="flex flex-col gap-0.5 overflow-y-auto flex-1 px-2 py-2">{t6}{t7}</div>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t5 || $[26] !== t8) {
    t9 = <div className="flex flex-col h-full">{t5}{t8}</div>;
    $[25] = t5;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
}